import { useInterval } from "@chakra-ui/react"
import { useRouter } from "next/router"
import Script from "next/script"
import { useState } from "react"
import { useRecoilState } from "recoil"
import domainsConfig from "../../../../domains.config"
import { AUTH_TTL } from "../../../common/constants/constants"
import { useAuth } from "../../../common/hooks/useGigya"
import { gigyaIsReadyState } from "../../../common/store/authStore"
import AccountDTO from "../../../common/types/gigya/account"
import { getEnvSpecificKey } from "../../../common/utils/configuration-utils"
import { getAccountInfo, logout } from "../../../common/utils/gigya-utils"
import { extractLanguage } from "../../../common/utils/locale-utils"
import { isSecureRoute } from "../../../common/utils/router-utils"

const GigyaScript = () => {
  const [gigyaIsLoaded, setGigyaIsLoaded] = useState(false)
  const [gigyaIsReady, setGigyaIsReady] = useRecoilState(gigyaIsReadyState)
  const { accountInfo, setAccountInfo, resetAccountInfo } = useAuth()
  const gigyaApiKey = getEnvSpecificKey(process.env.NEXT_PUBLIC_GIGYA_CONFIG)
  const router = useRouter()
  const languageCode = extractLanguage(router.locale)

  const domain = domainsConfig.brandDomains
    .find((brandDomain) => {
      if (brandDomain.brand === router.query.brand && brandDomain.locales.includes(router.locale)) return brandDomain
    })
    ?.domains[0]?.replace("catalog.", "")

  /* Callback to handle blogal onLogin event fired from Gigya */
  const onLoginCallback = (eventObj: AccountDTO) => {
    /* Get the user data we are interested in from the object passed to the callback */
    const userAccountInfo = {
      firstName: eventObj?.profile?.firstName ?? "",
      lastName: eventObj?.profile?.lastName ?? "",
      email: eventObj?.profile?.email ?? "",
      country: eventObj?.profile?.country ?? "",
      settings: eventObj?.data?.conf_onlinecatalog ? JSON.parse(eventObj?.data?.conf_onlinecatalog) : null,
      userId: eventObj?.UID ?? "",
      loginAt: new Date(),
    }

    /* Set the user data in global state and cookie */
    setAccountInfo(userAccountInfo)
  }

  /* Callback to handle glogal onLogout event fired from Gigya */
  const onLogoutCallback = () => {
    /* Clear the user data from global state and cookie */
    resetAccountInfo()

    /* If user is on authenticated route, redirect him to the homepage */
    if (isSecureRoute(router.asPath)) {
      router.push("/")
    }
  }

  const init = async () => {
    /* Attach handlers for global Gigya events */
    window.gigya.accounts.addEventHandlers({
      onLogin: onLoginCallback, // Fired from Gigya every time there is a successful login
      onLogout: onLogoutCallback, // Fired from Gigya every time there is a successful logout
    })

    /* This is the generic success code value returned from Gigya for all events */
    const GIGYA_SUCCESS_CODE = 0
    /* Get fresh account info from Gigya */
    const accountResponse = await getAccountInfo()
    /* If user session is expired Gigya will respond with non zero error code */
    const isLoggedIn = accountResponse?.errorCode === GIGYA_SUCCESS_CODE

    /* Perform some extra checks for logged in users */
    if (isLoggedIn) {
      /* Check if user has requested account deletion */
      if (accountResponse.data && accountResponse.data.isDeletionRequested) {
        await logout()
      }

      /* Check cookie expiration time and if cookie has expired, logout the user */
      const expirationTime = new Date()
      expirationTime.setTime(expirationTime.getTime() - AUTH_TTL * 1000)

      if (expirationTime > new Date(accountInfo.loginAt)) {
        await logout()
      }
    }

    /* If Gigya indicates that user is logged out, but we still have user data stored in cookie,
      logut the user in our application and clear the cookie */
    if (!isLoggedIn && accountInfo.userId) {
      onLogoutCallback()
    }

    /* Indicate that Gigya is initialized and ready to be used */
    setGigyaIsReady(true)
    console.log("Gigya is ready")
  }

  /* Load Gigya & set the ready state */
  useInterval(
    async () => {
      if (gigyaIsLoaded && window.gigya && window.gigya.isReady) {
        await init()
      }
    },
    !gigyaIsReady ? 200 : null
  )

  /* For China "zh-CN" we don't use the generic domain for gigya */
  const gigyaUrl = router.locale === "zh-CN" ? "cdns.cn1.sapcdm.cn" : `login-eu.${domain}`

  return (
    gigyaApiKey && (
      <Script
        src={`https://${gigyaUrl}/JS/gigya.js?apikey=${gigyaApiKey}&lang=${languageCode}`}
        onLoad={() => setGigyaIsLoaded(true)}
        strategy="afterInteractive"
      />
    )
  )
}

export default GigyaScript
